import {
	ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";

interface PDAlertTableProps {
	data: any[];
	columns: any[];
	rowSelectionEnabled: boolean;
	onRowSelect: (rowId: string | null) => void;
	serverSideSearch?: boolean;
	onServerSearch?: (columnId: string, value: string) => void;
	isLoading?: boolean;
}

const AdminTables = ({
	data,
	columns,
	rowSelectionEnabled = false,
	onRowSelect,
	serverSideSearch = false,
	onServerSearch,
	isLoading = false,
}: PDAlertTableProps) => {
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [searchInputValues, setSearchInputValues] = useState<Record<string, string>>({});

	const table = useReactTable({
		data,
		columns: columns,
		state: {
			sorting,
			columnFilters
		},
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		// skip filtering if using server side search
		enableFilters: !serverSideSearch,
	});

	const handleFilterChange = (columnId: string, value: string) => {
		// Update the input value state
		setSearchInputValues(prev => ({
			...prev,
			[columnId]: value
		}));
		
		if (serverSideSearch && onServerSearch) {
			// Call the server-side search handler
			onServerSearch(columnId, value);
			// Don't update columnFilters for server-side search
		} else {
			// Use client-side filtering
			setColumnFilters((old) => {
				const newFilters = old.filter((filter) => filter.id !== columnId);
				if (value) {
					newFilters.push({ id: columnId, value });
				}
				return newFilters;
			});
		}
	};

	const handleRowClick = (row: any) => {
		// Clear all selections first
		table.getRowModel().rows.forEach((r) => {
			if (r.getIsSelected()) {
				r.toggleSelected(false);
			}
		});
		// Select the clicked row
		row.toggleSelected(true);
		onRowSelect(row.original);
	};

	return (
		<div style={{ width: "95vw", overflowX: "auto" }}>
			<table className="table table-striped" style={{ width: "100%" }}>
				<thead>
					{table.getHeaderGroups().map((headerGroup) => (
						<tr key={headerGroup.id} style={{ borderBottomColor: "#0156A8" }}>
							{headerGroup.headers.map((header) => (
								<th
									key={header.id}
									
									style={{
										width: (header.column.columnDef as any).width,
										fontSize: "14px",
										fontWeight: 700,
										lineHeight: "17.6px",
										color: "#000000",
									}}
								>
									<p 
									onClick={header.column.getToggleSortingHandler()}
									style={{ cursor: "pointer", width: "100%" }}

									>
										{flexRender(
											header.column.columnDef.header,
											header.getContext()
										)}
										{header.column.getIsSorted() === "asc" ? (
											<i className="bi bi-sort-up mx-2"></i>
										) : header.column.getIsSorted() === "desc" ? (
											<i className="bi bi-sort-down mx-2"></i>
										) : null}
									</p>
									<input
										className="form-control"
										value={searchInputValues[header.id] || ""}
										onChange={(e) =>
											handleFilterChange(header.id, e.target.value)
										}
										placeholder={`Search ${header.id.split("_").join(" ")}`}
									/>
									{/* {{
										asc: " 🔼",
										desc: " 🔽",
									}[header.column.getIsSorted() as string] ?? null} */}
								</th>
							))}
						</tr>
					))}
				</thead>

				{
					isLoading ? (
						<tr>
							<td colSpan={columns.length} style={{ textAlign: "center" }}>
								<div
									className="d-flex justify-content-center align-items-center"
									style={{ height: "200px" }}
								>
									<div className="spinner-border text-primary" role="status">
										<span className="visually-hidden">Loading...</span>
									</div>
								</div>
							</td>
						</tr>
					) : (data.length === 0 && (
						<tr>
							<td colSpan={columns.length} style={{ textAlign: "center" }}>
								<h3
									className="text-secondary"
									style={{
										textAlign: "center",
										marginTop: "7%",
										marginBottom: "5%",
									}}
								>
									No data available
								</h3>
							</td>
						</tr>
					)
				)
				}
				
				{
					data.length > 0 && (
						<tbody>
							{table.getRowModel().rows.map((row) => {
								return (
									<tr
								key={row.id}
								onClick={() => handleRowClick(row)}
								style={{
									cursor: "pointer",
									backgroundColor: row.getIsSelected() ? "#f0f7ff" : "inherit",
								}}
							>
								{row.getVisibleCells().map((cell) => (
									<td
										key={cell.id}
										style={{
											padding: "8px",
											paddingBottom: "28px",
											fontSize: "14px",
											fontWeight: 400,
										}}
									>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</td>
								))}
							</tr>
						);
						})}
					</tbody>
				)}
			</table>
		</div>
	);
};

export default AdminTables;
